// importacion de pinia
import { defineStore } from 'pinia';
import { markRaw } from 'vue';

// importacion de servicios
import { routeClient, transactionClient } from '@/api';

// importacion de tipos
import { type IRoutesModule, type SetRoutesResponse } from './types';
import {
  type PaymentTypeCurrency,
  type RouteProductType,
} from '@/types/RouteTransferType';
import { type CurrencySeparatorConfig } from '@/types/currency';

// importacion de helpers
import { separatorDecimal } from '@/helpers/formatCurrency';

/**
 * Obtiene la config con mayor precision decimal entre los payment types de un producto.
 * @param bucket - Configs por payment type de un producto.
 * @returns La config de mayor decimalLimit, o undefined si el bucket esta vacio.
 */
function getMaxDecimalConfig(
  bucket: Partial<Record<PaymentTypeCurrency, CurrencySeparatorConfig>>
): CurrencySeparatorConfig | undefined {
  const configs = Object.values(bucket);
  if (!configs.length) return undefined;

  return configs.reduce((max, config) =>
    config.decimalLimit > max.decimalLimit ? config : max
  );
}

/**
 * Vacía la cola del store y pide en un solo request las monedas encoladas.
 * @param state - State del store de rutas; se muta en el lugar para conservar la reactividad.
 * @returns Promesa que resuelve al aplicar el response y rechaza, quitando el fallback de las monedas del batch, si el request falla.
 */
async function flushCurrencyConfigQueue(state: IRoutesModule): Promise<void> {
  const codes = [...state.currenciesConfigQueue];
  state.currenciesConfigQueue.clear();
  state.currenciesConfigBatch = null;

  try {
    const { data } = await routeClient.getCurrencyConfig(codes.join(','));

    state.currenciesConfig = data.reduce((acc, current) => {
      const {
        currencyCode,
        productCode,
        paymentTypeCode,
        limitDecimal,
        thousandSeparator,
        decimalSeparator,
      } = current;

      const productType = productCode || 'ANY';
      const paymentType = paymentTypeCode ?? 'ANY';

      if (!acc[currencyCode]) {
        acc[currencyCode] = {} as {
          [productType in RouteProductType]: {
            [paymentType in PaymentTypeCurrency]: CurrencySeparatorConfig;
          };
        };
      }

      if (!acc[currencyCode][productType]) {
        acc[currencyCode][productType] = {} as {
          [paymentType in PaymentTypeCurrency]: CurrencySeparatorConfig;
        };
      }

      acc[currencyCode][productType][paymentType] = {
        decimalLimit: Number(limitDecimal),
        thousandSeparator,
        decimalSeparator,
      };

      return acc;
    }, state.currenciesConfig);

    data.forEach(({ currencyCode, productCode }) => {
      const productType = productCode || 'ANY';
      const bucket = state.currenciesConfig[currencyCode]?.[productType];
      if (!bucket || bucket.ANY) return;

      const maxConfig = getMaxDecimalConfig(bucket);
      if (maxConfig) bucket.ANY = { ...maxConfig };
    });
  } catch (e: unknown) {
    state.currenciesConfig = Object.fromEntries(
      Object.entries(state.currenciesConfig).filter(
        ([code]) => !codes.includes(code)
      )
    );
    throw e;
  }
}

export const useRoutesStore = defineStore('routes', {
  state: (): IRoutesModule => ({
    routes: [],
    routesInFlight: null,
    currenciesConfig: {},
    currenciesConfigInFlight: markRaw(new Map<string, Promise<void>>()),
    currenciesConfigQueue: markRaw(new Set<string>()),
    currenciesConfigBatch: null,
  }),
  actions: {
    async setRoutes(): Promise<SetRoutesResponse> {
      if (this.routes.length) {
        return { data: this.routes };
      }

      if (this.routesInFlight) {
        return this.routesInFlight;
      }

      const request = (async (): Promise<SetRoutesResponse> => {
        try {
          const res = await transactionClient.getRoutes();
          this.routes = res.data;
          return { data: res.data };
        } finally {
          this.routesInFlight = null;
        }
      })();

      this.routesInFlight = markRaw(request);
      return request;
    },
    async setExternalRoutes() {
      const res = await transactionClient.getExternalRoutes();
      this.routes = res.data;
      return res;
    },
    /**
     * Agrupa por tick las monedas pedidas en un solo request, con fallback optimista hasta que llega el response.
     * Un código vacío o `undefined` se ignora; una moneda cacheada o en vuelo no se reenvía.
     *
     * @param currencyCode - código de la moneda (ej. `USD`); puede llegar vacío mientras no hay moneda seleccionada
     * @returns promesa que resuelve cuando la moneda quedó cargada o cacheada, y rechaza si falla el batch
     */
    async setCurrencyConfig(currencyCode: string | undefined): Promise<void> {
      const code = currencyCode?.trim();
      if (!code) return;

      const pending = this.currenciesConfigInFlight.get(code);
      if (pending) return pending;

      if (this.currenciesConfig[code]) return;

      const { decimalLimit, thousandSeparator, decimalSeparator } =
        separatorDecimal(code);

      this.currenciesConfig[code] = {
        ANY: {
          ANY: {
            decimalLimit,
            thousandSeparator,
            decimalSeparator,
          },
        },
      } as {
        [productType in RouteProductType]: {
          [paymentType in PaymentTypeCurrency]: CurrencySeparatorConfig;
        };
      };

      this.currenciesConfigQueue.add(code);

      if (!this.currenciesConfigBatch) {
        this.currenciesConfigBatch = markRaw(
          Promise.resolve().then(() => flushCurrencyConfigQueue(this))
        );
      }

      const batch = this.currenciesConfigBatch;
      this.currenciesConfigInFlight.set(code, batch);

      try {
        await batch;
      } finally {
        this.currenciesConfigInFlight.delete(code);
      }
    },
  },
  getters: {
    getRoutes: (state) => state.routes,
    getRoutesByProduct(state) {
      return (product: RouteProductType) =>
        state.routes
          .filter((item) =>
            product === 'P2P'
              ? item.product === product && item.country.alpha2Code === 'WA'
              : item.product === product
          )
          .sort((a, b) => a.country?.nameEN.localeCompare(b.country.nameEN));
    },
  },
});
